import { createClientId } from "./id";

function isoNow() {
  return new Date().toISOString();
}

export function exportWorkflowJSON({ name, nodes, edges }) {
  const workflowId = createClientId("workflow");
  const createdAt = isoNow();
  const updatedAt = isoNow();
  const stages = (nodes || []).map((n) => ({
    id: n.id,
    name: n.data?.name || "Stage",
    description: n.data?.description || "",
    color: n.data?.color || "#6366f1",
    position: n.position || { x: 0, y: 0 },
    // whether this stage should be sent for review when reached
    isReassignable: !!n.data?.isReassignable,
    maxConcurrentAssignees: n.data?.maxConcurrentAssignees || 1,
    assigneeRoles: n.data?.assigneeRoles || [],
    estimatedDuration: n.data?.estimatedDuration || 0,
    durationUnit: n.data?.durationUnit || "Days",
    deadlineType: n.data?.deadlineType || "None",
    slaWarningThreshold: n.data?.slaWarningThreshold || 80,
    requiresApproval: !!n.data?.requiresApproval,
    approvalType: n.data?.approvalType || "Any Assignee",
    completionCriteria: n.data?.completionCriteria || "",
    autoAdvance: !!n.data?.autoAdvance,
    notifyOnEntry: !!n.data?.notifyOnEntry,
    notifyOnExit: !!n.data?.notifyOnExit,
    isVisible: n.data?.isVisible !== false,
    // whether to send this stage for review
    sendForReview: !!n.data?.sendForReview,
    // branching when this stage has 2+ outgoing connections:
    // CHOICE = reviewer picks one branch, PARALLEL = all branches start
    branchMode: n.data?.branchMode === "PARALLEL" ? "PARALLEL" : "CHOICE",
    // list of things to be done in this stage (text fields or file uploads)
    tasks: (n.data?.tasks || []).map((t) => ({
      id: t.id || createClientId("task"),
      type: t.type || "text", // 'text' or 'file'
      title: t.title || "",
      mandatory: !!t.mandatory,
      // allowed file types for 'file' tasks, e.g. ['pdf','docx']
      fileTypes: t.fileTypes || [],
    })),
  }));

  const connections = (edges || []).map((e) => ({
    id: e.id,
    sourceStageId: e.source,
    targetStageId: e.target,
    label: e.label || "",
  }));

  return { workflowId, name, createdAt, updatedAt, stages, connections };
}

export function validateWorkflowJSON(obj) {
  if (!obj) return { ok: false, error: "Empty object" };
  if (!obj.name) return { ok: false, error: "Missing workflow name" };
  if (!Array.isArray(obj.stages))
    return { ok: false, error: "Missing or invalid stages array" };
  if (!Array.isArray(obj.connections))
    return { ok: false, error: "Missing or invalid connections array" };

  // optional: validate tasks structure if present
  for (const s of obj.stages) {
    if (s.tasks && !Array.isArray(s.tasks))
      return { ok: false, error: `Invalid tasks for stage ${s.id}` };
  }
  return { ok: true };
}

export function importWorkflowJSON(obj) {
  const nodes = (obj.stages || []).map((s) => ({
    id: s.id || createClientId("stage"),
    type: "stage",
    position: s.position || { x: 0, y: 0 },
    data: {
      name: s.name || "Stage",
      description: s.description || "",
      color: s.color || "#6366f1",
      isReassignable: !!s.isReassignable,
      maxConcurrentAssignees: s.maxConcurrentAssignees || 1,
      assigneeRoles: s.assigneeRoles || [],
      estimatedDuration: s.estimatedDuration || 0,
      durationUnit: s.durationUnit || "Days",
      deadlineType: s.deadlineType || "None",
      slaWarningThreshold: s.slaWarningThreshold || 80,
      requiresApproval: !!s.requiresApproval,
      approvalType: s.approvalType || "Any Assignee",
      completionCriteria: s.completionCriteria || "",
      autoAdvance: !!s.autoAdvance,
      notifyOnEntry: !!s.notifyOnEntry,
      notifyOnExit: !!s.notifyOnExit,
      isVisible: s.isVisible !== false,
      // restore sendForReview flag
      sendForReview: !!s.sendForReview,
      branchMode: s.branchMode === "PARALLEL" ? "PARALLEL" : "CHOICE",
      // restore tasks (ensure shape)
      tasks: (s.tasks || []).map((t) => ({
        id: t.id || createClientId("task"),
        type: t.type || "text",
        title: t.title || "",
        mandatory: !!t.mandatory,
        fileTypes: t.fileTypes || [],
      })),
    },
  }));

  const edges = (obj.connections || []).map((c) => ({
    id: c.id || createClientId("edge"),
    source: c.sourceStageId,
    target: c.targetStageId,
    label: c.label || "",
    animated: false,
    style: { stroke: "#888" },
  }));

  return { nodes, edges };
}
